import dayjs from 'dayjs'
import { makeAutoObservable, runInAction } from 'mobx'

import { notificationService } from '@/Core'
import { createFormContext } from '@/Core/UI/Form/Providers'
import { serverMessage, serverStatus } from '@/Ship/Helpers/serverError'

import { violationsOf } from '../../VerificationButtons/classifyVerificationViolations'
import {
    CONTRACT_NUMBER_MAX_LENGTH,
    DOCUMENT_NUMBER_MAX_LENGTH,
    GENERATE_DOCUMENT_TEXTS as T,
} from '../GenerateDocument'
import { GenerateDocumentTask } from '../Tasks/GenerateDocumentTask'
import { GetLastContractTask } from '../Tasks/GetLastContractTask'
import { LoadDocumentTypesTask } from '../Tasks/LoadDocumentTypesTask'

import type { CrmDocumentGenerationConfig, GenerationDocumentType } from '../GenerateDocument'
import type { FormStore } from '@/Core/UI/Form/Stores/FormStore'

/** Поля тіла генерації, помилки яких лягають під поля шухляди. */
const FORM_FIELDS = new Set(['legalEntity', 'number', 'date', 'contractNumber', 'contractDate'])

/** Uuid із того, що тримає select ядра: вкладений об'єкт, IRI або сам uuid. */
const uuidOf = (value: unknown): string | null => {
    if (value !== null && typeof value === 'object') {
        const record = value as Record<string, unknown>

        if (typeof record['uuid'] === 'string' && record['uuid'] !== '') return record['uuid']

        return uuidOf(record['@id'])
    }

    if (typeof value === 'string' && value !== '') return value.split('/').filter(Boolean).pop() ?? null

    return null
}

/** Дата документа — без часу й поясу: `YYYY-MM-DD`. */
const dateOf = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null

    const date = dayjs(value as string)

    return date.isValid() ? date.format('YYYY-MM-DD') : null
}

/**
 * Генерація документа з картки власника (`SPEC-012` §9.2–§9.3): типи для
 * меню, шухляда з формою, запит і відповідь.
 *
 *  - **меню** — типи свого провайдера з прапорцем картки (P-12, N-19); немає
 *    типів — немає й групи меню, ручне «Додати документ» лишається (N-10);
 *  - **шухляда** — юрособа першою (N-12): її вибір підставляє номер і дату з
 *    останнього договору з нею (P-16); «З печаткою та підписом» увімкнено
 *    (P-18). Заявка (`SPEC-025` §3.1.1): договорів у неї немає, номер за
 *    замовчуванням — Ід заявки, і ще два необов'язкові поля — «Номер
 *    договору», «Дата договору»;
 *  - **успіх** — шухляда закривається, «Документ … успішно згенеровано.»,
 *    список перечитується (P-21);
 *  - **помилка** — показується в шухляді, кнопка знову активна (N-11); 422 полів
 *    тіла — під полями.
 */
export class GenerateDocumentStore {
    types: GenerationDocumentType[] = []
    typesLoaded = false

    /** Тип, з якого генеруємо; `null` — шухляда закрита */
    activeType: GenerationDocumentType | null = null
    form: FormStore | null = null
    pending = false
    error: string | null = null

    /** Номер запиту договору: відповідь на попередній вибір юрособи не перезаписує новіший */
    private contractRequest = 0

    /** Що зробити після успіху — перечитати список вкладки */
    private onGenerated: (() => void) | null = null

    constructor(
        readonly config: CrmDocumentGenerationConfig,
        private readonly ownerField: string
    ) {
        makeAutoObservable<GenerateDocumentStore, 'contractRequest' | 'ownerField' | 'onGenerated'>(this, {
            config: false,
            ownerField: false,
            contractRequest: false,
            onGenerated: false,
        })
    }

    get isOpen(): boolean {
        return this.activeType !== null && this.form !== null
    }

    /** Типи для меню — раз на вкладку; збій — меню без групи генерації. */
    async loadTypes(): Promise<void> {
        try {
            const types = await LoadDocumentTypesTask(this.config)

            runInAction(() => {
                this.types = types
                this.typesLoaded = true
            })
        } catch {
            runInAction(() => {
                this.types = []
                this.typesLoaded = true
            })
        }
    }

    /**
     * @param defaultNumber номер документа за замовчуванням (заявка — її Ід,
     *                      `SPEC-021` §8.8); людина може його змінити
     */
    open(typeUuid: string, ownerUuid: string, onGenerated: () => void, defaultNumber?: string): void {
        const type = this.types.find((item) => item.uuid === typeUuid)

        if (!type) return

        this.onGenerated = onGenerated
        this.error = null
        this.activeType = type
        this.form = createFormContext({
            fields: {
                legalEntity: {
                    label: this.config.legalEntityLabel,
                    type: 'select',
                    resource: 'Setting.ListLegalEntity',
                    isRequired: true,
                },
                number: {
                    label: T.number,
                    isRequired: true,
                    inputProps: { maxLength: DOCUMENT_NUMBER_MAX_LENGTH },
                },
                date: {
                    label: T.date,
                    type: 'date',
                    isRequired: true,
                },
                ...(this.config.withContractTerms
                    ? {
                          contractNumber: {
                              label: T.contractNumber,
                              inputProps: { maxLength: CONTRACT_NUMBER_MAX_LENGTH },
                          },
                          contractDate: {
                              label: T.contractDate,
                              type: 'date',
                          },
                      }
                    : {}),
                isStamp: {
                    label: T.isStamp,
                    type: 'switch',
                },
            },
            initialValues: {
                isStamp: true,
                ...(defaultNumber !== undefined && defaultNumber !== '' ? { number: defaultNumber } : {}),
            },
            onChange: ({ changedValues }: { changedValues: Record<string, unknown> }) => {
                if ('legalEntity' in changedValues) void this.applyLastContract(ownerUuid, changedValues['legalEntity'])
            },
            onSubmit: ({ values }: { values: Record<string, unknown> }) => this.submit(ownerUuid, values),
        }) as FormStore
    }

    close(): void {
        if (this.pending) return

        this.activeType = null
        this.form = null
        this.error = null
    }

    /** «Згенерувати»: ядрова форма спершу перевіряє обов'язкові поля. */
    submitForm(): void {
        if (this.pending) return

        void this.form?.submit()
    }

    /**
     * Номер і дата — з останнього договору з обраною юрособою (P-16). Немає
     * договору — поля лишаються як є (так і в легасі).
     */
    private async applyLastContract(ownerUuid: string, legalEntity: unknown): Promise<void> {
        const legalEntityUuid = uuidOf(legalEntity)
        const request = ++this.contractRequest
        const contractsRestUrl = this.config.contractsRestUrl

        if (legalEntityUuid === null || contractsRestUrl === null) return

        try {
            const terms = await GetLastContractTask(contractsRestUrl, this.ownerField, ownerUuid, legalEntityUuid)

            if (terms === null || request !== this.contractRequest || this.form === null) return

            if (terms.number !== null) this.form.setValue('number', terms.number)
            if (terms.date !== null) this.form.setValue('date', dayjs(terms.date))
        } catch {
            // Підказка, а не умова: без договору номер і дату вводять руками
        }
    }

    private async submit(ownerUuid: string, values: Record<string, unknown>): Promise<void> {
        const type = this.activeType
        const legalEntity = uuidOf(values['legalEntity'])
        const date = dateOf(values['date'])
        const number = typeof values['number'] === 'string' ? values['number'].trim() : ''
        const contractNumber = typeof values['contractNumber'] === 'string' ? values['contractNumber'].trim() : ''

        if (type === null || this.pending) return

        // Порожнє ядрова форма вже не пропустила — тут лише те, що не
        // розібралося (дата не дата, юрособа без uuid)
        if (legalEntity === null || date === null || number === '') {
            this.error = T.contactAdmin
            return
        }

        this.pending = true
        this.error = null

        try {
            const document = await GenerateDocumentTask(type.uuid, {
                owner: ownerUuid,
                number,
                date,
                legalEntity,
                isStamp: values['isStamp'] === true,
                ...(this.config.withContractTerms
                    ? {
                          contractNumber: contractNumber !== '' ? contractNumber : null,
                          contractDate: dateOf(values['contractDate']),
                      }
                    : {}),
            })

            const title = typeof document?.['title'] === 'string' ? document['title'] : number

            const onGenerated = this.onGenerated

            runInAction(() => {
                this.pending = false
            })
            this.close()
            notificationService.showSuccess(T.successTitle, T.success(title))
            onGenerated?.()
        } catch (error: unknown) {
            runInAction(() => {
                this.pending = false
                this.applyError(error)
            })
        }
    }

    /** 422 полів — під поля; решта — текстом сервера в шухляді (N-11). */
    private applyError(error: unknown): void {
        const violations = serverStatus(error) === 422 ? violationsOf((error as { response?: unknown }).response) : []
        const onFields = violations.filter(({ field }) => FORM_FIELDS.has(field))

        if (onFields.length > 0 && this.form) {
            this.form.setErrors(Object.fromEntries(onFields.map(({ field, message }) => [field, message])))
        }

        this.error =
            onFields.length === violations.length && onFields.length > 0 ? null : serverMessage(error, T.contactAdmin)
    }
}
